<template>
  <VRow class="mt-4" data-testid="incertitudes">
    <ActionTablePage
      data-testid="table-incertitudes-list"
      :items="incertitudesList"
      :headers="incertitudeHeaders"
      :filters="incertitudeFilters"
      filter-as-menu
      local-filter-mode
      class="mb-4"
    >
      <template #subheader-left
        ><h2>{{ t("elementsIncertitudes") }}</h2></template
      >
      <template #[`item.incertitudeDescription`]="{ item }">
        <span>{{ truncateText(item.incertitudeDescription, 30) }}</span>
      </template>
      <template #[`item.reason`]="{ item }">
        <span>{{ getElementReason(item) }}</span>
      </template>
      <template #[`item.actions`]="{ item }">
        <SocioIconBtn icon="mdi-pencil-outline" @click="openDialog(item)" />
      </template>
    </ActionTablePage>
  </VRow>
  <VRow>
    <VCol cols="12">
      <h2>{{ t("otherIncertitudes") }}</h2>
    </VCol>
    <VRow>
      <VCol cols="12">
        <ActionTablePage
          data-testid="table-other-incertitudes"
          :items="OthetIncertitudesList"
          :headers="otherIncertitudesHeaders"
          :filters="otherIncertitudesFilters"
          filter-as-menu
          local-filter-mode
          class="mb-4"
        >
          <template #subheader-left>
            <SocioBtn
              prepend-icon="mdi-plus"
              :text="t('add')"
              data-testid="button-add-incertitude"
              @click="openDialog(null)"
            />
          </template>
          <template #[`item.description`]="{ item }">
            <span>{{ truncateText(item.description, 30) }}</span>
          </template>
          <template #[`item.actions`]="{ item }">
            <SocioIconBtn
              icon="mdi-trash-can-outline"
              class="mr-1"
              @click="
                removeModal = true;
                itemToDelete = item;
              "
            />
            <SocioDialogValidation v-model="removeModal" :title="t('removeModalTitle')">
              <template #text>
                <SocioAlert color="#fcb902" variant="tonal" icon="mdi-alert-box-outline">
                  <h3 class="alert-title">{{ t("alert") }}</h3>
                  <p>{{ t("deletionText") }}</p>
                </SocioAlert>
              </template>
              <template #validation>
                <SocioBtn
                  prepend-icon="mdi-trash-can-outline"
                  :text="t('delete')"
                  class="btn-icon--primary"
                  color="#D80F0C"
                  @click="deleteIncertitude"
                />
              </template>
            </SocioDialogValidation>
            <SocioIconBtn icon="mdi-pencil-outline" @click="openDialog(item)" />
          </template>
        </ActionTablePage>
      </VCol>
    </VRow>
  </VRow>
  <IncertitudeModal v-if="modelValue" v-model="modelValue" :incertitude="incertitude" />
</template>
<script setup>
import { ref, onMounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRepo } from "pinia-orm";
import { useIncertitudeStore } from "@/store/modules/incertitude";
import { useNotificationsStore } from "@/store/modules/notification";
import { useMissionStore } from "@/store/modules/mission";
import { useCartographyStore } from "@/store/modules/cartography";
import { useTestimonialStore } from "@/store/modules/testimonials";
import IncertitudeModal from "@/components/ssp/missions/mission/mission-global-synthesis/IncertitudeModal.vue";
import Incertitude from "@/models/Incertitude";
import { useRoute } from "vue-router";
import { VCol } from "vuetify/components";
import ActionTablePage from "@/components/base-components/ActionTablePage.vue";
import { incertitudeCategories } from "@/constants/incertitude";
import { truncateText } from "@/utils/utils";
const route = useRoute();
const cartographyStore = useCartographyStore();
const missionStore = useMissionStore();
const testimonialsStore = useTestimonialStore();

const { t } = useI18n();
const notificationsStore = useNotificationsStore();
const incertitudeStore = useIncertitudeStore();
const IncertitudeRepo = useRepo(Incertitude);
const modelValue = ref(false);
const removeModal = ref(false);
const itemToDelete = ref(null);
const incertitude = ref(null);
const incertitudesList = computed(() => [
  ...cartographyStore.getAllElements,
  ...testimonialsStore.getHistoricalItems,
]);
const incertitudeFilters = [
  {
    type: "text",
    label: t("label"),
    key: "label",
  },
  {
    type: "text",
    label: t("description"),
    key: "incertitudeDescription",
  },
  {
    type: "choice",
    label: t("category"),
    key: "incertitudeCategory",
    options: incertitudeCategories,
  },
];
const OthetIncertitudesList = computed(() => IncertitudeRepo.orderBy("createdAt").get());

const incertitudeHeaders = [
  { title: t("label"), value: "label", width: "22%" },
  { title: t("reason"), value: "reason", width: "22%" },
  { title: t("description"), value: "incertitudeDescription", width: "22%" },
  { title: t("category"), value: "incertitudeCategory", width: "22%" },
  { width: "10%", title: "", value: "actions", align: "end" },
];
const otherIncertitudesHeaders = [
  {
    title: t("description"),
    value: "description",
    width: "40%",
  },
  {
    title: t("category"),
    value: "category",
    width: "40%",
  },
  {
    width: "10%",
    title: "",
    value: "actions",
    align: "end",
  },
];
const otherIncertitudesFilters = [
  {
    type: "text",
    label: t("description"),
    key: "description",
  },
  {
    type: "choice",
    label: t("category"),
    key: "category",
    options: incertitudeCategories,
  },
];
const getElementReason = (item) => {
  if (["BUILDING", "ZONE"].includes(item.typology)) {
    return "Absence d'accessibilité si Tag non ajouté à la main";
  } else if (item.type === "TANK") {
    return "Contenu ou volume inconnu si Tag non ajouté à la main";
  } else if (["DRAINAGE", "TREATMENT_STRUCTURE", "BACKFILL", "PAINT_BOOTH"].includes(item.type)) {
    return "Profondeur inconnue si Tag non ajouté à la main";
  }
  return "Tag ajouté à la main";
};
const deleteIncertitude = async () => {
  await incertitudeStore.deleteIncertitude(itemToDelete.value);
  removeModal.value = false;
};
const openDialog = (item) => {
  incertitude.value = item;
  modelValue.value = true;
};

onMounted(async () => {
  try {
    if (!cartographyStore.siteUuid) {
      await cartographyStore.getCurrentSite(missionStore.currentMissionUuid);
    }
    const fetchRemarquableElement = async (type) => {
      const filters = {
        projectId: missionStore.currentMissionUuid,
        tagsType: "incertitude",
      };
      let secondaryFilters = {};
      if (cartographyStore.siteUuid) {
        secondaryFilters = {
          site: cartographyStore.siteUuid,
          tagsType: "incertitude",
        };
      }
      await cartographyStore.fetchAllItems({
        type: type,
        filters,
        secondaryFilters,
      });
    };

    const fetchHistoricalElements = async (type) => {
      const filters = {
        project_id: missionStore.currentMissionUuid,
        tags_type: "incertitude",
      };
      await testimonialsStore.fetchAllItems({
        type: type,
        filters,
      });
    };
    await incertitudeStore.fetchIncertitudes();
    await Promise.all([
      fetchRemarquableElement("COMPONENT"),
      fetchRemarquableElement("BUILDING"),
      fetchRemarquableElement("ZONE"),
      fetchRemarquableElement("OUTDOOR_SPACE"),
      fetchRemarquableElement("BASIAS_BASOL_SIS_NEARBY_SITE_OTHER"),

      fetchHistoricalElements("ACCIDENTS"),
      fetchHistoricalElements("ACTIVITY"),
      fetchHistoricalElements("POLLUTION"),
      fetchHistoricalElements("PRODUCT"),
      fetchHistoricalElements("ICPE"),
      fetchHistoricalElements("BOMBINGS"),
    ]);
  } catch (error) {
    notificationsStore.showErrorNotification(t("fetchError"));
    console.error("Error fetching incertitudes:", error);
  }
  await cartographyStore.getCurrentSite(route.params.missionId);
});
</script>
<style scoped>
.alert-title {
  color: #fcb902;
}
</style>
<i18n>
{
  "fr": {
    "add": "Déclarer incertitude",
    "label": "Dénomination",
    "reason": "Raison",
    "description": "Description",
    "category": "Catégorie",
    "elementsIncertitudes": "Incertitudes sur les éléments remarquable et historiques",
    "otherIncertitudes": "Autres incertitudes",
    "fetchError": "Erreur lors de la récupération des incertitudes",
    "removeModalTitle": "Alerte suppression",
    "alert": "Vous aller supprimer une incertitude",
    "deletionText": "Cette action est irréversible. Elle entrainera la suppression de toutes les informations liées à cette incertitude.",
    "delete": "Supprimer"
  },
  "en": {
    "add": "Declare incertitude",
    "label": "Label",
    "reason": "Reason",
    "description": "Description",
    "category": "Category",
    "elementsIncertitudes": "Incertitudes on remarkable and historical elements",
    "otherIncertitudes": "Other incertitudes",
    "fetchError": "Error while fetching incertitudes",
    "removeModalTitle": "Deletion alert",
    "alert": "You are going to delete an incertitude",
    "deletionText": "This action is irreversible. It will result in the deletion of all information related to this incertitude.",
    "delete": "Delete"
  }
}
</i18n>
